/**
 * Right column of the loadout screen (08 §2.5): who is going, what the load weighs and what that
 * does to him, what it costs and what RP is left, what the objective needs, and the warnings.
 * Warnings inform and never block: the screen's DEPLOY stays live whatever this column says.
 */

import type { PriceList } from '../../../meta/api';
import type { ArchetypeId } from '../../../sim/types';
import { box, el, panel, show } from '../dom';
import type { LoadoutState, PackItem } from './model';
import { carriedWeight, encumbrance, loadoutCost } from './model';
import { loadoutWarnings, missionNeeds, needText, warningText } from './warnings';

export interface SummaryOptions {
  /** `SGT "VIPER" MARLOWE`, or whatever identifies the ranger. */
  rangerName: string;
  archetype: ArchetypeId;
  prices: PriceList;
  /** Classic packs free (04 §1): no cost, no RP line. */
  free: boolean;
  /** RP the operation holds; ignored when `free`. */
  rp: number;
  name(item: PackItem): string;
}

export interface SummaryView {
  readonly el: HTMLElement;
  update(state: LoadoutState): void;
  /** Show a message in the error strip (a thrown deploy error, a blocked step); '' hides it. */
  setError(message: string): void;
}

function line(label: string): { row: HTMLElement; value: HTMLElement } {
  const value = el('span', 'menu-paper');
  return { row: box('menu-load-sum-row', el('span', 'menu-dim', label), value), value };
}

/** "RUNS 15% SLOWER, TIRES 30% FASTER" or "UNENCUMBERED" (02 §4-5). Pure. */
export function encumbranceText(carriedWeight: number): string {
  const e = encumbrance(carriedWeight);
  return e.over <= 0 ? 'UNENCUMBERED' : `RUNS ${e.runSlower}% SLOWER, TIRES ${e.tiresFaster}% FASTER`;
}

export function summaryView(options: SummaryOptions): SummaryView {
  const p = panel('LOAD', 'menu-load-summary menu-grow');
  const weight = line('CARRIED');
  const burden = el('div', 'menu-load-burden menu-prose');
  const cost = line('COST');
  const left = line('RP LEFT');
  const warnings = el('div', 'menu-load-warnings');
  warnings.setAttribute('role', 'status');
  const error = el('div', 'menu-error menu-hidden');
  error.setAttribute('role', 'alert');

  p.body.append(el('div', 'menu-load-who menu-paper', options.rangerName), weight.row, burden);
  if (options.free) p.body.append(el('div', 'menu-good', 'FREE PACKING'));
  else p.body.append(cost.row, left.row);

  const needs = missionNeeds(options.archetype);
  if (needs.length > 0) {
    p.body.append(el('div', 'menu-dossier-key', 'OBJECTIVE REQUIRES'));
    for (const need of needs) p.body.append(el('div', 'menu-load-need menu-prose', needText(need, options.name)));
  }
  p.body.append(warnings, error);

  return {
    el: p.panel,
    update(state): void {
      const carried = carriedWeight(state, options.prices);
      weight.value.textContent = `${carried}/${options.prices.carryCap}`;
      burden.textContent = encumbranceText(carried);
      burden.classList.toggle('menu-warn', encumbrance(carried).over > 0);

      const total = loadoutCost(state, options.prices, options.free);
      cost.value.textContent = `${total} RP`;
      left.value.textContent = `${options.rp - total} RP`;
      left.value.classList.toggle('menu-bad', options.rp - total < 0);

      const list = loadoutWarnings(options.archetype, state).map((w) => {
        const tone = `menu-load-warning-${w.severity}`;
        const mark = w.severity === 'critical' ? '!! ' : w.severity === 'caution' ? '! ' : '';
        return el('div', `menu-load-warning menu-prose ${tone}`, `${mark}${warningText(w, options.name)}`);
      });
      warnings.replaceChildren(...list);
    },
    setError(message): void {
      error.textContent = message;
      show(error, message !== '');
    },
  };
}
